import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography } from '../../../ui/core';
import GoogleMap from '../../../ui/core/Map';
import { Marker } from '@react-google-maps/api';
import { useFormatters } from '../../../hooks';
import { CIRCLE_SYMBOL_PATH } from '../../../ui/core/mapSymbolPaths';

import pickupSvg from '../../../assets/map/pickup.svg';
import dropoffSvg from '../../../assets/map/dropoff.svg';
import grayPinSvg from '../../../assets/map/grayPin.svg';

// Group location points into time buckets and calculate directions
const groupPointsByTime = (points, bucketMinutes = 2, maxPoints = 500) => {
  if (!points || points.length === 0) return [];
  
  // Sort points by timestamp (reverse chronological order - most recent first)
  const sortedPoints = [...points].sort((a, b) => 
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
  
  const bucketsMap = new Map();
  
  sortedPoints.forEach(point => {
    const timestamp = new Date(point.timestamp);
    // Create bucket key by rounding to nearest bucketMinutes
    const bucketKey = Math.floor(timestamp.getTime() / (bucketMinutes * 60 * 1000));
    
    if (!bucketsMap.has(bucketKey)) {
      bucketsMap.set(bucketKey, point);
    }
  });
  
  // Convert buckets back to array and limit to maxPoints
  const groupedPoints = Array.from(bucketsMap.values());
  const limitedPoints = groupedPoints.slice(-maxPoints);
  
  // Calculate direction for each point (except the last one)
  const pointsWithDirection = limitedPoints.map((point, index) => {
    let direction = null;
    
    // Calculate direction to the next point (more recent in time)
    if (index > 0) {
      const nextPoint = limitedPoints[index - 1];
      const lat1 = point.coordinate.lat;
      const lon1 = point.coordinate.lon;
      const lat2 = nextPoint.coordinate.lat;
      const lon2 = nextPoint.coordinate.lon;
      
      // Calculate bearing (direction) in degrees
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const lat1Rad = lat1 * Math.PI / 180;
      const lat2Rad = lat2 * Math.PI / 180;
      
      const y = Math.sin(dLon) * Math.cos(lat2Rad);
      const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) - Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon);
      
      direction = Math.atan2(y, x) * 180 / Math.PI;
      // Normalize to 0-360 degrees
      direction = (direction + 360) % 360;
    }
    
    return {
      ...point,
      direction
    };
  });
  
  return pointsWithDirection;
};


// Path literals, not `window.google.maps.SymbolPath` — that read is what threw
// "Cannot read properties of undefined (reading 'CIRCLE')" on prod; see
// ui/core/mapSymbolPaths.js. The arrow points north in symbol units, and Google
// rotates symbols clockwise from north, matching our bearing.
const FORWARD_CLOSED_ARROW_PATH = 'M 0,-2 L 1.5,1.5 L -1.5,1.5 Z';

const LOCATION_DOT_ICON = Object.freeze({
  path: CIRCLE_SYMBOL_PATH,
  scale: 6,
  fillColor: '#8B5CF6', // Original solid purple color
  fillOpacity: 0.8,
  strokeWeight: 0,
});

// Create arrow icon for directional markers
const createArrowIcon = (direction) => ({
  path: FORWARD_CLOSED_ARROW_PATH,
  scale: 4,
  fillColor: '#6D28D9', // Darker purple
  fillOpacity: 0.8,
  strokeWeight: 0,
  rotation: direction || 0,
});

const TripLocationHistory = ({ trip, locationData = [], fullTripData = null }) => {
  const [map, setMap] = useState(null);
  const f = useFormatters();

  // Group points into 2-minute buckets
  const groupedLocationData = groupPointsByTime(locationData, 2);

  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  // Process waypoints for pickup/dropoff markers
  const waypoints = useMemo(() => {
    return trip.waypoints?.map((w) => {
      const point = w[0];
      
      // Find the ride associated with this waypoint
      const ride = trip.rides?.find((r) => {
        const rideId = r.id || r.rideId;
        return rideId === point.ride_id;
      });
      
      const riderName = `${ride?.rider?.firstName || ''} ${ride?.rider?.lastName || ''}`.trim() || 'Unknown';
      
      return {
        position: { lat: point.coordinates.lat, lng: point.coordinates.lon },
        type: point.type,
        rideId: point.ride_id,
        riderName: riderName,
      };
    }) || [];
  }, [trip.waypoints, trip.rides]);

  const cancelledRidesPickups = useMemo(() => {
    const cancelledRides = trip?.cancelledRides || fullTripData?.cancelledRides || [];
    return cancelledRides.map((ride) => {
      if (ride.pickUp && ride.pickUp.coords && ride.pickUp.coords.coordinates) {
        const riderName = `${ride?.rider?.firstName || ''} ${ride?.rider?.lastName || ''}`.trim() || 'Unknown';
        
        return {
          position: { lat: ride.pickUp.coords.coordinates[1], lng: ride.pickUp.coords.coordinates[0] },
          type: 'cancelled-pickup',
          rideId: ride.id || ride._id,
          riderName: riderName,
        };
      }
      return null;
    }).filter(Boolean) || [];
  }, [trip?.cancelledRides, fullTripData?.cancelledRides]);


  useEffect(() => {
    if (map && (waypoints.length > 0 || groupedLocationData.length > 0 || cancelledRidesPickups.length > 0) && window.google && window.google.maps) {
      const bounds = new window.google.maps.LatLngBounds();
      
      // Add pickup waypoints only
      const pickupWaypoints = waypoints.filter(point => point.type === 'pick-up');
      
      pickupWaypoints.forEach(point => {
        bounds.extend(new window.google.maps.LatLng(
          point.position.lat, 
          point.position.lng
        ));
      });
      
      // Add cancelled rides pickup locations
      cancelledRidesPickups.forEach(pickup => {
        bounds.extend(new window.google.maps.LatLng(
          pickup.position.lat, 
          pickup.position.lng
        ));
      });
      
      // Add the earliest location point
      if (groupedLocationData.length > 0) {
        const earliestPoint = groupedLocationData[groupedLocationData.length - 1]; // Last item is earliest due to reverse chronological order
        bounds.extend(new window.google.maps.LatLng(
          earliestPoint.coordinate.lat,
          earliestPoint.coordinate.lon
        ));
      }
      
      // Use fitBounds with minimal padding
      map.fitBounds(bounds, 50);
    }
  }, [map, waypoints, groupedLocationData, cancelledRidesPickups]);

  const formatDateTime = (timestamp) => {
    return f.date.toTimezoneLocaleString(
      timestamp,
      'US/Central',
      f.date.DateFormats.DATETIME_MED_WITH_SEC
    );
  };


  if (!locationData.length) {
    return (
      <Box sx={{ width: 384, height: 384, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">No location history available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: 384, height: 384 }}>
      <GoogleMap 
        zoom={16} 
        center={{ lat: 0, lng: 0 }} 
        mapContainerStyle={{ width: 384, height: 384 }}
        onLoad={onMapLoad}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          zoomControl: true,
          gestureHandling: 'cooperative',
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'transit',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'landscape',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'landscape',
              elementType: 'geometry',
              stylers: [{ color: '#f5f5f5' }]
            },
            {
              featureType: 'water',
              elementType: 'geometry',
              stylers: [{ color: '#e3f2fd' }]
            },
            {
              featureType: 'road',
              elementType: 'geometry',
              stylers: [{ color: '#e0e0e0' }]
            },
            {
              featureType: 'road.highway',
              elementType: 'geometry',
              stylers: [{ color: '#d0d0d0' }]
            }
          ]
        }}
      >
        {/* Waypoint markers (pickup/dropoff) */}
        {waypoints.map((point, idx) => (
          <Marker 
            key={`waypoint-${idx}`} 
            position={point.position} 
            icon={point.type === 'pick-up' ? pickupSvg : dropoffSvg} 
            zIndex={2}
            title={`${point.type === 'pick-up' ? 'Pickup' : 'Dropoff'} - ${point.riderName}`}
          />
        ))}
        
        
        {/* Cancelled rides pickup markers */}
        {cancelledRidesPickups.map((pickup, idx) => (
          <Marker 
            key={`cancelled-pickup-${idx}`} 
            position={pickup.position} 
            icon={grayPinSvg} 
            zIndex={2}
            title={`Cancelled ride pickup - ${pickup.riderName}`}
          />
        ))}
        
        {/* Location history markers */}
        {groupedLocationData.map((point, index) => {
          // Show arrows every 10th point (or for the first point if it has direction)
          const shouldShowArrow = (index % 10 === 0 && point.direction !== null) ||
                                 (index === 0 && point.direction !== null);

          const icon = shouldShowArrow ? createArrowIcon(point.direction) : LOCATION_DOT_ICON;
          
          return (
            <Marker
              key={`location-${index}`}
              position={{ lat: point.coordinate.lat, lng: point.coordinate.lon }}
              title={formatDateTime(point.timestamp)}
              icon={icon}
              zIndex={shouldShowArrow ? 3 : 1}
            />
          );
        })}
      </GoogleMap>
    </Box>
  );
};

export default TripLocationHistory; 